/** @format */

import { Grid, MenuItem, TextField } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Breadcrumb from 'src/components/Breadcrumb';
import Page from 'src/components/Page';
import { Button, Toggle } from 'src/components';
import ExtranetEditorLanguageDropdown from 'src/components/extranet/ExtranetEditorLanguageDropdown';
import { clearTopicEditorErrors, createTopic, getTopicTree, updateTopic } from 'src/slices/topics';
import { useDispatch, useSelector } from 'src/store';
import { flattenTopicTreeForAdmin, formatAdminBrandLabel } from 'src/utils/administration';
import { getExtranetEditorLanguageOptions, resolveExtranetSelectableLocale } from 'src/utils/extranetLanguages';
import { resolveSelectedLanguage } from 'src/utils/locale';

const TOPICS_LIST_ROUTE = '/administration/topics';
const BRAND_CODE_MAP = {
  // Brand is stored/sent as string codes (not numeric).
  ate: 'ate',
  vdo: 'vdo',
  continental: 'continental',
  // Backward-compatible alias (older data may use `conti`)
  conti: 'continental'
};

export default function TopicEditor() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { topicID, routeLocale } = useParams();
  const { t, i18n } = useTranslation();
  const languageOptions = useMemo(() => getExtranetEditorLanguageOptions(t), [t]);
  const { topicTree, createTopicLoading, createTopicError, updateTopicLoading, updateTopicError } = useSelector(
    state => state.topics
  );
  const adminSelectedLanguage = useSelector(state => state.topics.selectedLanguage);
  const isEditMode = Boolean(topicID);

  const [formValues, setFormValues] = useState({
    name: '',
    displayName: '',
    description: '',
    published: false,
    brand: '',
    parentKey: 0,
    roles: []
  });

  const defaultLocale = useMemo(
    () => resolveExtranetSelectableLocale(routeLocale || adminSelectedLanguage || resolveSelectedLanguage() || 'en'),
    [routeLocale, adminSelectedLanguage]
  );
  const [locale, setLocale] = useState(defaultLocale);
  const hasInitializedCreateLocaleRef = useRef(false);

  useEffect(() => {
    const lang = typeof i18n.language === 'string' && i18n.language.trim() ? i18n.language : 'en';
    dispatch(getTopicTree());
  }, [dispatch, i18n.language]);

  useEffect(() => {
    return () => {
      dispatch(clearTopicEditorErrors());
    };
  }, [dispatch]);

  const flatTopics = useMemo(() => flattenTopicTreeForAdmin(topicTree) ?? [], [topicTree]);

  useEffect(() => {
    if (isEditMode) {
      hasInitializedCreateLocaleRef.current = false;
      return;
    }
    if (hasInitializedCreateLocaleRef.current) return;
    setLocale(defaultLocale);
    hasInitializedCreateLocaleRef.current = true;
  }, [isEditMode, defaultLocale]);

  const brandOptions = useMemo(() => {
    const seen = new Set();
    const options = [];

    flatTopics.forEach(topic => {
      const brand = topic?.brand;
      if (!brand || seen.has(brand)) return;
      seen.add(brand);
      options.push({ value: brand, label: formatAdminBrandLabel(brand) });
    });

    return options;
  }, [flatTopics]);

  const parentOptions = useMemo(() => {
    const limitedLevelOptions = flatTopics
      .filter(topic => topic.depth <= 2)
      .filter(topic => Number.isFinite(Number(topic.id)))
      .map(topic => ({
        value: Number(topic.id),
        label: `${'— '.repeat(Math.min(topic.depth, 2))}${topic.displayName || topic.name}`
      }));

    return [{ value: 0, label: t('administrationPage.parentKeyRootOption') }, ...limitedLevelOptions];
  }, [flatTopics, t]);

  useEffect(() => {
    if (!formValues.brand && brandOptions.length > 0) {
      setFormValues(prev => ({ ...prev, brand: brandOptions[0].value }));
    }
  }, [brandOptions, formValues.brand]);

  const editingTopic = useMemo(() => {
    if (!isEditMode) return null;
    return flatTopics.find(topic => String(topic.id) === String(topicID))?.topic || null;
  }, [flatTopics, isEditMode, topicID]);

  const duplicateTopic = useMemo(() => {
    if (isEditMode) return null;
    return location?.state?.duplicateTopic || null;
  }, [isEditMode, location]);

  useEffect(() => {
    if (!isEditMode || !editingTopic) return;

    const nextParent =
      Array.isArray(editingTopic.parents) && editingTopic.parents.length > 0 ? Number(editingTopic.parents[0]) || 0 : 0;

    setFormValues(prev => ({
      ...prev,
      name: editingTopic.name || '',
      displayName: editingTopic.displayName || '',
      description: editingTopic.description || '',
      published: Boolean(editingTopic.published),
      brand: editingTopic.brand || '',
      parentKey: nextParent,
      roles: Array.isArray(editingTopic.roles)
        ? editingTopic.roles.map(role => Number(role)).filter(Number.isFinite)
        : []
    }));

    // Set locale from existing topic if available
    if (editingTopic.locale) {
      const resolvedLocale = resolveExtranetSelectableLocale(editingTopic.locale);
      if (languageOptions.some(option => option.value === resolvedLocale)) {
        setLocale(resolvedLocale);
      }
    }
  }, [editingTopic, isEditMode, languageOptions]);

  useEffect(() => {
    if (isEditMode || !duplicateTopic) return;

    const nextParent =
      Array.isArray(duplicateTopic.parents) && duplicateTopic.parents.length > 0
        ? Number(duplicateTopic.parents[0]) || 0
        : 0;

    setFormValues(prev => ({
      ...prev,
      name: duplicateTopic.name || '',
      displayName: duplicateTopic.displayName || '',
      description: duplicateTopic.description || '',
      published: Boolean(duplicateTopic.published),
      brand: duplicateTopic.brand || '',
      parentKey: nextParent,
      roles: Array.isArray(duplicateTopic.roles)
        ? duplicateTopic.roles.map(role => Number(role)).filter(Number.isFinite)
        : []
    }));

    // Set locale from duplicate topic if available
    if (duplicateTopic.locale) {
      const resolvedLocale = resolveExtranetSelectableLocale(duplicateTopic.locale);
      if (languageOptions.some(option => option.value === resolvedLocale)) {
        setLocale(resolvedLocale);
      }
    }
  }, [duplicateTopic, isEditMode, languageOptions]);

  const handleFieldChange = event => {
    const { name, value } = event.target;
    setFormValues(prev => ({
      ...prev,
      [name]: name === 'parentKey' ? Number(value) || 0 : value
    }));
  };

  const handlePublishedChange = event => {
    const checked = Boolean(event?.target?.checked);
    setFormValues(prev => ({ ...prev, published: checked }));
  };

  const handleLocaleChange = newLocale => {
    setLocale(resolveExtranetSelectableLocale(newLocale, defaultLocale));
  };

  const toBrandCode = brandValue => {
    if (brandValue == null) return '';

    const raw = String(brandValue).trim();
    if (!raw) return '';

    const key = raw.toLowerCase();

    // Backward compatibility: older topic data might still use numeric brand codes.
    const parsed = Number(key);
    if (Number.isFinite(parsed)) {
      if (parsed === 1) return 'ate';
      if (parsed === 2) return 'vdo';
      if (parsed === 3) return 'continental';
    }

    return BRAND_CODE_MAP[key] ?? raw;
  };

  const handleSave = async () => {
    const topicData: {
      locale: string;
      name: string;
      displayName: string;
      description: string;
      brand: string;
      parents: number[];
      published: boolean;
      id?: number;
    } = {
      locale: locale || 'en',
      name: formValues.name.trim(),
      displayName: formValues.displayName.trim(),
      description: formValues.description.trim(),
      brand: toBrandCode(formValues.brand),
      // roles: Array.isArray(formValues.roles) ? formValues.roles : [],
      parents: formValues.parentKey ? [formValues.parentKey] : [],
      published: formValues.published
    };

    // Add id for update mode
    if (isEditMode) {
      topicData.id = Number(topicID);
    }

    const payload = {
      topic: [topicData]
    };

    const lang = locale || 'en';

    try {
      if (isEditMode) {
        await dispatch(updateTopic(topicID, payload, lang));
      } else {
        await dispatch(createTopic(payload, lang));
      }
      navigate(TOPICS_LIST_ROUTE);
    } catch (error) {
      // Error state is managed in redux.
    }
  };

  const handleCancel = () => {
    navigate(TOPICS_LIST_ROUTE);
  };

  const submitError = isEditMode ? updateTopicError : createTopicError;
  const submitLoading = isEditMode ? updateTopicLoading : createTopicLoading;

  return (
    <Page
      className="administration-topic-create-page"
      title={isEditMode ? t('administrationPage.editTopicTitle') : t('administrationPage.createTopicTitle')}>
      <Breadcrumb
        data={[
          { title: t('administrationPage.topicsTitle'), link: TOPICS_LIST_ROUTE },
          { title: isEditMode ? t('administrationPage.editTopicTitle') : t('administrationPage.createTopicTitle') }
        ]}
        additionalClasses="container--narrow"
      />

      <Grid className="administration-topic-create-page__container container container--narrow">
        <h1 className="administration-topic-create-page__heading">
          {isEditMode ? t('administrationPage.editTopicTitle') : t('administrationPage.createTopicTitle')}
        </h1>

        <ExtranetEditorLanguageDropdown
          languageOptions={languageOptions}
          locale={locale}
          defaultLocale={defaultLocale}
          onLocaleChange={handleLocaleChange}
          triggerId="topic-editor-language-trigger"
        />

        <section className="administration-topic-create-page__section">
          <div className="administration-topic-create-page__field">
            <span className="administration-topic-create-page__label">{t('administrationPage.fieldNameRequired')}</span>
            <TextField
              id="topic-name"
              name="name"
              value={formValues.name}
              onChange={handleFieldChange}
              variant="outlined"
              fullWidth
              className="administration-topic-create-page__text-field"
            />
          </div>

          <div className="administration-topic-create-page__field">
            <span className="administration-topic-create-page__label">{t('administrationPage.fieldDisplayName')}</span>
            <TextField
              id="topic-display-name"
              name="displayName"
              value={formValues.displayName}
              onChange={handleFieldChange}
              variant="outlined"
              fullWidth
              className="administration-topic-create-page__text-field"
            />
          </div>

          <div className="administration-topic-create-page__field">
            <span className="administration-topic-create-page__label">{t('administrationPage.fieldDescription')}</span>
            <TextField
              id="topic-description"
              name="description"
              value={formValues.description}
              onChange={handleFieldChange}
              variant="outlined"
              fullWidth
              multiline
              className="administration-topic-create-page__text-field administration-topic-create-page__textarea-field"
            />
          </div>
        </section>

        <section className="administration-topic-create-page__settings">
          <h2 className="administration-topic-create-page__settings-title">{t('common.settings')}</h2>

          <div className="administration-topic-create-page__settings-grid">
            <div className="administration-topic-create-page__settings-label-wrap">
              <p className="administration-topic-create-page__settings-label">{t('common.status')}</p>
              {/* <p className="administration-topic-create-page__settings-help">
                {t('administrationPage.fieldStatusDescription')}
              </p> */}
            </div>
            <div className="administration-topic-create-page__settings-control">
              <Toggle
                name="published"
                checked={formValues.published}
                onChange={handlePublishedChange}
                label={t('administrationPage.statusPublic')}
              />
            </div>

            <p className="administration-topic-create-page__settings-label">{t('administrationPage.fieldBrand')}</p>
            <div className="administration-topic-create-page__settings-control">
              <TextField
                select
                name="brand"
                value={formValues.brand}
                onChange={handleFieldChange}
                variant="standard"
                fullWidth
                className="administration-topic-create-page__select-field">
                {brandOptions.length === 0 ? (
                  <MenuItem value="">{t('administrationPage.selectPlaceholder')}</MenuItem>
                ) : (
                  brandOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </div>

            <p className="administration-topic-create-page__settings-label">
              {t('administrationPage.fieldParentTopic')}
            </p>
            <div className="administration-topic-create-page__settings-control">
              <TextField
                select
                name="parentKey"
                value={formValues.parentKey}
                onChange={handleFieldChange}
                variant="standard"
                fullWidth
                className="administration-topic-create-page__select-field">
                {parentOptions.length === 0 ? (
                  <MenuItem value="">{t('administrationPage.selectPlaceholder')}</MenuItem>
                ) : (
                  parentOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </div>
          </div>
        </section>

        <div className="administration-topic-create-page__actions">
          {submitError ? (
            <p className="administration-topic-create-page__submit-error">{t('common.unknownError')}</p>
          ) : null}
          <Button
            type="button"
            label={t('common.button.save')}
            onClick={handleSave}
            disabled={!formValues.name.trim() || submitLoading}
            additionalClassName="administration-topic-create-page__save-btn"
          />
          <Button
            type="button"
            secondary
            label={t('common.button.cancel')}
            onClick={handleCancel}
            disabled={submitLoading}
            additionalClassName="administration-topic-create-page__cancel-btn"
          />
        </div>
      </Grid>
    </Page>
  );
}
